import { useEffect } from 'react';
import type { NotificationType } from '@prisma/client';
import { useSession } from 'next-auth/react';

import { messageHandlers } from 'components/messages/message-handlers';
import { getOrganizationChannelName } from 'utils/ably/get-channel-names';
import { closeAblyClient, getAblyClient } from 'utils/ably/get-client';

const isNotificationType = (name: string): name is NotificationType => {
  return name in messageHandlers;
};

const isNotificationPayload = (
  data: unknown,
): data is { itemId: string; message: string } => {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return typeof d.itemId === 'string' && typeof d.message === 'string';
};

export const MessageCenter = () => {
  const session = useSession();
  const organizationId = session.data?.user?.organizationId;

  useEffect(() => {
    if (!organizationId) {
      closeAblyClient();
      return;
    }
    const ablyClient = getAblyClient(organizationId);
    const channel = ablyClient.channels.get(
      getOrganizationChannelName(organizationId),
    );
    const handleMessage = (message: { name?: string; data?: unknown }) => {
      // The organization channel receives both notification and automation job update events.
      // We only handle notifications here; other listeners (e.g. automation job hook) use the same channel.
      const name = message.name;
      if (
        name &&
        isNotificationType(name) &&
        isNotificationPayload(message.data)
      ) {
        messageHandlers[name](message.data);
      }
    };
    void channel.subscribe(handleMessage);
    return () => {
      channel.unsubscribe(handleMessage);
      closeAblyClient();
    };
  }, [organizationId]);

  return <div />;
};
